// @spec IAM-STR-017
'use client';

import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

interface CollapsibleProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
}

interface CollapsibleTriggerProps {
  children: React.ReactNode;
  className?: string;
  asChild?: boolean;
}

interface CollapsibleContentProps {
  children: React.ReactNode;
  className?: string;
}

const CollapsibleContext = React.createContext<{
  open: boolean;
  toggle: () => void;
}>({
  open: false,
  toggle: () => undefined,
});

/**
 * Collapsible container component.
 */
function Collapsible({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  children,
  className,
}: CollapsibleProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const toggle = React.useCallback(() => {
    if (isControlled) {
      onOpenChange?.(!open);
    } else {
      setInternalOpen((prev) => !prev);
    }
  }, [isControlled, open, onOpenChange]);

  return (
    <CollapsibleContext.Provider value={{ open, toggle }}>
      <div className={className}>{children}</div>
    </CollapsibleContext.Provider>
  );
}

/**
 * Trigger element for collapsible.
 */
function CollapsibleTrigger({ children, className, asChild }: CollapsibleTriggerProps) {
  const { open, toggle } = React.useContext(CollapsibleContext);

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ onClick?: () => void }>, {
      onClick: toggle,
    });
  }

  return (
    <button
      type="button"
      onClick={toggle}
      className={cn(
        'flex w-full items-center justify-between py-2 text-sm font-medium transition-all',
        '[&[data-state=open]_[data-collapsible-chevron]]:rotate-180',
        className
      )}
      data-state={open ? 'open' : 'closed'}
    >
      {children}
      <ChevronDown
        data-collapsible-chevron
        className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200"
      />
    </button>
  );
}

/**
 * Content container for collapsible.
 */
function CollapsibleContent({ children, className }: CollapsibleContentProps) {
  const { open } = React.useContext(CollapsibleContext);

  if (!open) return null;

  return (
    <div
      className={cn(
        'overflow-hidden transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',
        className
      )}
      data-state="open"
    >
      {children}
    </div>
  );
}

export { Collapsible, CollapsibleContent, CollapsibleTrigger };
